import { addDays, subDays } from 'date-fns';
import type { CollectionCase, PromiseToPay } from '@/types/collections';
import type {
  CasesByStatus,
  CollectionAlert,
  CollectionsDashboardMetrics,
  PromiseKPIData,
  QueueSummaryStats,
  ResponseRateByChannel,
  TeamMemberPerformance,
  WeeklyCollectionData,
} from '@/types/collections-extended';

// Mock Collection Cases
export const mockCollectionCases: CollectionCase[] = [
  {
    id: '1',
    caseId: 'AR-00001',
    customerId: 'CUST-001',
    customerAccountNumber: 'ACC-001',
    customerName: 'Acme Corporation',
    contactName: 'John Smith',
    customerEmail: 'accounts@acme.com',
    customerPhone: '+44 20 7946 0101',
    priority: 'High',
    status: 'Active',
    strategy: { id: '1', name: 'Standard Collection', type: 'Standard' },
    strategyPhase: { currentPhase: 3, totalPhases: 5, phaseName: 'Payment Arrangement' },
    amount: 45000,
    daysInCollection: 45,
    nextAction: 'Follow-up call',
    nextActionDate: new Date(),
    riskLevel: 'High',
    createdAt: subDays(new Date(), 45),
    updatedAt: new Date(),
    assignedTo: 'Sarah Johnson',
  },
  {
    id: '2',
    caseId: 'AR-00002',
    customerId: 'CUST-002',
    customerAccountNumber: 'ACC-002',
    customerName: 'TechStart Inc',
    contactName: 'Sarah Mitchell',
    customerEmail: 'billing@techstart.com',
    customerPhone: '+44 20 7946 0102',
    priority: 'Medium',
    status: 'Active',
    strategy: { id: '2', name: 'VIP Collection', type: 'VIP' },
    strategyPhase: { currentPhase: 2, totalPhases: 4, phaseName: 'Follow-up & Negotiation' },
    amount: 28500,
    daysInCollection: 30,
    nextAction: 'Send payment reminder',
    nextActionDate: addDays(new Date(), 2),
    riskLevel: 'Medium',
    createdAt: subDays(new Date(), 30),
    updatedAt: subDays(new Date(), 1),
    assignedTo: 'Michael Chen',
  },
  {
    id: '3',
    caseId: 'AR-00003',
    customerId: 'CUST-003',
    customerAccountNumber: 'ACC-003',
    customerName: 'Global Retail Ltd',
    contactName: 'David Chen',
    customerEmail: 'finance@globalretail.com',
    customerPhone: '+44 121 496 0103',
    priority: 'High',
    status: 'Active',
    strategy: { id: '3', name: 'Intensive Collection', type: 'Intensive' },
    strategyPhase: { currentPhase: 4, totalPhases: 5, phaseName: 'Final Notice' },
    amount: 67200,
    daysInCollection: 60,
    nextAction: 'Escalate to manager',
    nextActionDate: subDays(new Date(), 1),
    riskLevel: 'High',
    createdAt: subDays(new Date(), 60),
    updatedAt: subDays(new Date(), 2),
    assignedTo: 'Sarah Johnson',
  },
  {
    id: '4',
    caseId: 'AR-00004',
    customerId: 'CUST-004',
    customerAccountNumber: 'ACC-004',
    customerName: 'BuildCo Construction',
    contactName: 'Robert Williams',
    customerEmail: 'ap@buildco.com',
    customerPhone: '+44 161 850 0104',
    priority: 'Low',
    status: 'Active',
    strategy: { id: '1', name: 'Standard Collection', type: 'Standard' },
    strategyPhase: { currentPhase: 1, totalPhases: 5, phaseName: 'Initial Contact' },
    amount: 12300,
    daysInCollection: 15,
    nextAction: 'Send email reminder',
    nextActionDate: addDays(new Date(), 5),
    riskLevel: 'Low',
    createdAt: subDays(new Date(), 15),
    updatedAt: subDays(new Date(), 3),
    assignedTo: 'Emily Rodriguez',
  },
  {
    id: '5',
    caseId: 'AR-00005',
    customerId: 'CUST-005',
    customerAccountNumber: 'ACC-005',
    customerName: 'MediCare Solutions',
    contactName: 'Emily Rodriguez',
    customerEmail: 'billing@medicare-sol.com',
    customerPhone: '+44 113 496 0105',
    priority: 'Medium',
    status: 'On Hold',
    strategy: { id: '1', name: 'Standard Collection', type: 'Standard' },
    strategyPhase: { currentPhase: 2, totalPhases: 5, phaseName: 'Follow-up & Negotiation' },
    amount: 34800,
    daysInCollection: 38,
    nextAction: 'Awaiting dispute resolution',
    nextActionDate: addDays(new Date(), 7),
    riskLevel: 'Medium',
    createdAt: subDays(new Date(), 38),
    updatedAt: subDays(new Date(), 5),
    assignedTo: 'Michael Chen',
  },
  {
    id: '6',
    caseId: 'AR-00006',
    customerId: 'CUST-006',
    customerAccountNumber: 'ACC-006',
    customerName: 'FoodService Partners',
    contactName: 'James Patterson',
    customerEmail: 'accounts@foodservice.com',
    customerPhone: '+44 117 929 0106',
    priority: 'High',
    status: 'Active',
    strategy: { id: '4', name: 'High Risk Collection', type: 'High Risk' },
    strategyPhase: { currentPhase: 5, totalPhases: 5, phaseName: 'Escalation' },
    amount: 89400,
    daysInCollection: 75,
    nextAction: 'Legal review',
    nextActionDate: new Date(),
    riskLevel: 'High',
    createdAt: subDays(new Date(), 75),
    updatedAt: new Date(),
    assignedTo: 'David Kim',
  },
  {
    id: '7',
    caseId: 'AR-00007',
    customerId: 'CUST-007',
    customerAccountNumber: 'ACC-007',
    customerName: 'AutoParts Warehouse',
    contactName: 'Amanda Foster',
    customerEmail: 'finance@autoparts.com',
    customerPhone: '+44 113 378 0107',
    priority: 'Medium',
    status: 'Active',
    strategy: { id: '1', name: 'Standard Collection', type: 'Standard' },
    strategyPhase: { currentPhase: 2, totalPhases: 5, phaseName: 'Follow-up & Negotiation' },
    amount: 21600,
    daysInCollection: 22,
    nextAction: 'Phone call',
    nextActionDate: addDays(new Date(), 1),
    riskLevel: 'Low',
    createdAt: subDays(new Date(), 22),
    updatedAt: subDays(new Date(), 1),
    assignedTo: 'Emily Rodriguez',
  },
  {
    id: '8',
    caseId: 'AR-00008',
    customerId: 'CUST-008',
    customerAccountNumber: 'ACC-008',
    customerName: 'Digital Marketing Pro',
    contactName: 'Thomas Anderson',
    customerEmail: 'billing@digitalmarketing.com',
    customerPhone: '+44 116 318 0108',
    priority: 'Low',
    status: 'Active',
    strategy: { id: '2', name: 'VIP Collection', type: 'VIP' },
    strategyPhase: { currentPhase: 1, totalPhases: 4, phaseName: 'Initial Contact' },
    amount: 8900,
    daysInCollection: 10,
    nextAction: 'Send payment link',
    nextActionDate: addDays(new Date(), 3),
    riskLevel: 'Low',
    createdAt: subDays(new Date(), 10),
    updatedAt: subDays(new Date(), 2),
    assignedTo: 'Sarah Johnson',
  },
  {
    id: '9',
    caseId: 'AR-00009',
    customerId: 'CUST-009',
    customerAccountNumber: 'ACC-009',
    customerName: 'Northern Logistics',
    contactName: 'Michael Thompson',
    customerEmail: 'contact@northernlogistics.com',
    customerPhone: '+44 191 605 0109',
    priority: 'High',
    status: 'Active',
    strategy: { id: '1', name: 'Standard Collection', type: 'Standard' },
    strategyPhase: { currentPhase: 4, totalPhases: 5, phaseName: 'Final Notice' },
    amount: 38778.0,
    daysInCollection: 47,
    nextAction: 'Send final notice',
    nextActionDate: new Date(),
    riskLevel: 'High',
    createdAt: subDays(new Date(), 47),
    updatedAt: new Date(),
    assignedTo: 'James Wilson',
  },
];

// Mock Promises to Pay
export const mockPromises: PromiseToPay[] = [
  {
    id: 'PRM-00001',
    customerId: 'CUST-001',
    customerName: 'Acme Corporation',
    customerAccountNumber: 'ACC-001',
    customerEmail: 'accounts@acme.com',
    customerPhone: '+44 20 7946 0101',
    caseId: 'case-1',
    caseNumber: 'AR-00001',
    promiseDate: addDays(new Date(), 5),
    promisedAmount: 45000,
    status: 'Active',
    confirmationMethod: 'Phone',
    createdBy: 'Sarah Johnson',
    createdAt: subDays(new Date(), 2),
    notes: 'Customer confirmed payment will be processed on promised date',
    invoices: [
      {
        id: 'INV-001',
        headerReference: 'INV-2024-001',
        invoiceDate: subDays(new Date(), 60),
        originalAmount: 45000,
        promisedAmount: 45000,
        currentStatus: 'Unpaid',
        balanceRemaining: 45000,
      },
    ],
    assignedUserId: 1,
    assignedUserName: 'Sarah Johnson',
    isAtRisk: false,
    brokenPromisesCount: 0,
  },
  {
    id: 'PRM-00002',
    customerId: 'CUST-009',
    customerName: 'RetailMax Inc',
    customerAccountNumber: 'ACC-009',
    customerEmail: 'finance@retailmax.com',
    customerPhone: '+44 20 7946 0102',
    caseId: 'case-9',
    caseNumber: 'AR-00009',
    promiseDate: subDays(new Date(), 3),
    promisedAmount: 32000,
    status: 'Broken',
    confirmationMethod: 'Email',
    createdBy: 'Michael Chen',
    createdAt: subDays(new Date(), 15),
    notes: 'Customer missed promised payment date',
    invoices: [
      {
        id: 'INV-002',
        headerReference: 'INV-2024-002',
        invoiceDate: subDays(new Date(), 45),
        originalAmount: 32000,
        promisedAmount: 32000,
        currentStatus: 'Unpaid',
        balanceRemaining: 32000,
      },
    ],
    daysOverdue: 3,
    assignedUserId: 2,
    assignedUserName: 'Michael Chen',
    isAtRisk: true,
    brokenPromisesCount: 1,
  },
  {
    id: 'PRM-00003',
    customerId: 'CUST-010',
    customerName: 'TechVentures LLC',
    customerAccountNumber: 'ACC-010',
    customerEmail: 'billing@techventures.com',
    customerPhone: '+44 121 496 0103',
    caseId: 'case-10',
    caseNumber: 'AR-00010',
    promiseDate: addDays(new Date(), 2),
    promisedAmount: 18500,
    status: 'Active',
    confirmationMethod: 'Portal',
    createdBy: 'Emily Rodriguez',
    createdAt: subDays(new Date(), 5),
    notes: 'Customer confirmed via customer portal',
    invoices: [
      {
        id: 'INV-003',
        headerReference: 'INV-2024-003',
        invoiceDate: subDays(new Date(), 30),
        originalAmount: 18500,
        promisedAmount: 18500,
        currentStatus: 'Unpaid',
        balanceRemaining: 18500,
      },
    ],
    assignedUserId: 3,
    assignedUserName: 'Emily Rodriguez',
    isAtRisk: false,
    brokenPromisesCount: 0,
  },
  {
    id: 'PRM-00004',
    customerId: 'CUST-011',
    customerName: 'Manufacturing Plus',
    customerAccountNumber: 'ACC-011',
    customerEmail: 'ap@mfgplus.com',
    customerPhone: '+44 161 850 0104',
    caseId: 'case-11',
    caseNumber: 'AR-00011',
    promiseDate: subDays(new Date(), 10),
    promisedAmount: 56000,
    status: 'Paid',
    confirmationMethod: 'Phone',
    createdBy: 'David Kim',
    createdAt: subDays(new Date(), 20),
    notes: 'Payment received in full',
    invoices: [
      {
        id: 'INV-004',
        headerReference: 'INV-2024-004',
        invoiceDate: subDays(new Date(), 50),
        originalAmount: 56000,
        promisedAmount: 56000,
        currentStatus: 'Paid',
        balanceRemaining: 0,
      },
    ],
    assignedUserId: 4,
    assignedUserName: 'David Kim',
    isAtRisk: false,
    brokenPromisesCount: 0,
  },
  {
    id: 'PRM-00005',
    customerId: 'CUST-012',
    customerName: 'ServicePro Group',
    customerAccountNumber: 'ACC-012',
    customerEmail: 'finance@servicepro.com',
    customerPhone: '+44 113 496 0105',
    caseId: 'case-12',
    caseNumber: 'AR-00012',
    promiseDate: addDays(new Date(), 1),
    promisedAmount: 24000,
    status: 'Active',
    confirmationMethod: 'Email',
    createdBy: 'Sarah Johnson',
    createdAt: subDays(new Date(), 7),
    notes: 'Customer requested payment plan, first installment due tomorrow',
    invoices: [
      {
        id: 'INV-005',
        headerReference: 'INV-2024-005',
        invoiceDate: subDays(new Date(), 35),
        originalAmount: 48000,
        promisedAmount: 24000,
        currentStatus: 'Partially Paid',
        balanceRemaining: 24000,
      },
    ],
    assignedUserId: 1,
    assignedUserName: 'Sarah Johnson',
    isAtRisk: false,
    brokenPromisesCount: 0,
  },
  {
    id: 'PRM-00006',
    customerId: 'CUST-013',
    customerName: 'HealthCare Systems',
    customerAccountNumber: 'ACC-013',
    customerEmail: 'billing@healthcare-sys.com',
    customerPhone: '+44 117 929 0106',
    caseId: 'case-13',
    caseNumber: 'AR-00013',
    promiseDate: subDays(new Date(), 5),
    promisedAmount: 41000,
    status: 'Broken',
    confirmationMethod: 'Phone',
    createdBy: 'Michael Chen',
    createdAt: subDays(new Date(), 18),
    notes: 'Second broken promise - escalation required',
    invoices: [
      {
        id: 'INV-006',
        headerReference: 'INV-2024-006',
        invoiceDate: subDays(new Date(), 55),
        originalAmount: 41000,
        promisedAmount: 41000,
        currentStatus: 'Unpaid',
        balanceRemaining: 41000,
      },
    ],
    daysOverdue: 5,
    assignedUserId: 2,
    assignedUserName: 'Michael Chen',
    isAtRisk: true,
    brokenPromisesCount: 2,
  },
];

// Dashboard Metrics
export const mockDashboardMetrics: CollectionsDashboardMetrics = {
  overview: {
    totalActiveCases: 127,
    totalOutstanding: 2847500,
    collectionRate: 78.5,
    avgDaysToPayment: 18,
  },
  trends: {
    collectionRateTrend: 2.5,
    avgDaysToPaymentTrend: -3,
  },
};

// Team Performance
export const mockTeamPerformance: TeamMemberPerformance[] = [
  {
    id: '1',
    name: 'Sarah Johnson',
    activeCases: 32,
    resolvedCases: 145,
    totalCollected: 1245000,
    successRate: 82.5,
    avgResponseTime: 4.2,
    overdueActions: 2,
  },
  {
    id: '2',
    name: 'Michael Chen',
    activeCases: 28,
    resolvedCases: 132,
    totalCollected: 1089000,
    successRate: 79.8,
    avgResponseTime: 5.1,
    overdueActions: 1,
  },
  {
    id: '3',
    name: 'Emily Rodriguez',
    activeCases: 25,
    resolvedCases: 118,
    totalCollected: 945000,
    successRate: 76.3,
    avgResponseTime: 4.8,
    overdueActions: 3,
  },
  {
    id: '4',
    name: 'David Kim',
    activeCases: 30,
    resolvedCases: 128,
    totalCollected: 1156000,
    successRate: 80.1,
    avgResponseTime: 4.5,
    overdueActions: 0,
  },
  {
    id: '5',
    name: 'Lisa Anderson',
    activeCases: 12,
    resolvedCases: 95,
    totalCollected: 782000,
    successRate: 74.2,
    avgResponseTime: 5.5,
    overdueActions: 4,
  },
];

// Collection Alerts
export const mockCollectionAlerts: CollectionAlert[] = [
  {
    id: '1',
    type: 'urgent',
    title: 'High Priority Cases',
    description: '8 cases require immediate attention',
    actionRequired: true,
    createdAt: new Date(),
    link: '/collections/queue?priority=High',
  },
  {
    id: '2',
    type: 'warning',
    title: 'Overdue Accounts Increasing',
    description: '15 accounts moved to 90+ days overdue',
    actionRequired: true,
    createdAt: subDays(new Date(), 1),
    link: '/customers/ageing-analysis',
  },
  {
    id: '3',
    type: 'info',
    title: 'Collection Rate Improving',
    description: 'Up 2.5% from last month',
    actionRequired: false,
    createdAt: subDays(new Date(), 2),
    link: '/collections/dashboard',
  },
];

// Cases by Status
export const mockCasesByStatus: CasesByStatus[] = [
  { status: 'Active', count: 89, totalAmount: 2145000, color: 'green' },
  { status: 'Pending', count: 23, totalAmount: 456000, color: 'amber' },
  { status: 'Resolved', count: 145, totalAmount: 3890000, color: 'blue' },
  { status: 'Disputed', count: 15, totalAmount: 246500, color: 'red' },
];

// Weekly Collection Data
export const mockWeeklyCollectionData: WeeklyCollectionData[] = [
  { week: 'Week 1', collected: 245000, target: 250000 },
  { week: 'Week 2', collected: 268000, target: 250000 },
  { week: 'Week 3', collected: 232000, target: 250000 },
  { week: 'Week 4', collected: 289000, target: 250000 },
];

// Response Rate by Channel
export const mockResponseRateByChannel: ResponseRateByChannel[] = [
  { channel: 'Email', responseRate: 45, totalSent: 450, totalResponses: 203 },
  { channel: 'Phone', responseRate: 72, totalSent: 280, totalResponses: 202 },
  { channel: 'SMS', responseRate: 38, totalSent: 320, totalResponses: 122 },
  { channel: 'Portal', responseRate: 65, totalSent: 180, totalResponses: 117 },
];

// Promise KPI Data
export const mockPromiseKPIData: PromiseKPIData = {
  activePromises: { count: 25, totalValue: 1245000 },
  brokenPromises: { count: 8, totalValue: 342000 },
  dueThisWeek: { count: 5, totalValue: 187500 },
  atRiskPromises: { count: 3 },
};

// Queue Summary Stats
export const mockQueueSummaryStats: QueueSummaryStats = {
  myCases: { total: 32, filtered: 32 },
  highPriority: 8,
  dueToday: 5,
  totalOutstanding: { amount: 1456000, avgDays: 28 },
};
